import React, {
  useState, useEffect, useCallback, useMemo
} from 'react';
/* react-map-gl's default entry point is the MAPBOX one: it does a dynamic
 * `import('mapbox-gl')` at module scope regardless of the `mapLib` prop. This app has
 * never used mapbox -- `package.json` overrides that dependency to an empty package --
 * so the import resolves to a module with no `Map`, and rendering dies on
 * "react_map_gl.default is not a constructor".
 *
 * `react-map-gl/maplibre` is the library's own entry for exactly this case. Same
 * component, same props, `import('maplibre-gl')` instead. The explicit `mapLib` prop
 * below is now redundant but is left in place as documentation of intent.
 */
import MapGL from 'react-map-gl/maplibre';
import maplibregl from 'maplibre-gl';
import {
  ArcLayer, DeckGL, FlyToInterpolator, GeoJsonLayer, H3HexagonLayer, LineLayer
} from 'deck.gl';

import { rgb } from 'd3';

import DisclaimerModal from './DisclaimerModal';
import TimelineScrubber from './TimelineScrubber';
import LogoPanel from './LogoPanel';
import ShiftHint from './ShiftHint';
import RightPanel from './RightPanel';
import { ATTRIBUTE_OPTIONS, IS_IPAD } from './utils';
import LeftPanel from './LeftPanel';
import { useSessionContext } from './SessionContext';
import MobilePanel from './MobilePanel';
import StatusBanner from './StatusBanner';

const ELEVATION_SCALE = 100000;

/* Administrative areas are extruded on a shorter scale than hexagons.
 *
 * An H3 res-4 cell is ~1,770 km2 and every cell is the same size, so one elevation scale
 * reads correctly everywhere. Administrative areas are not: a German *Kreis* and a
 * Western Australian local government area differ by three orders of magnitude in
 * footprint, and at the hexagon scale the small dense ones render as needles that hide
 * everything behind them. This is the largest scale at which central Europe still reads
 * as a surface rather than a pin cushion.
 */
const ADM_ELEVATION_SCALE = 30000;

const DEFAULT_VIEWSTATE = {
  longitude: -95.7129,
  latitude: 37.0902,
  zoom: 4,
  bearing: 0,
  pitch: 50,
};

/* An optional `#zoom/lat/lng/pitch/bearing` deep link, the convention OSM and Leaflet
 * use. It exists so a particular place can be opened directly -- `#7/51.2/7.0` lands on
 * the Ruhr, which is how the "does Germany render *Kreise* rather than the 38
 * *Regierungsbezirke*" question gets answered by looking rather than by querying.
 * Absent or malformed, the map opens where it always did.
 */
const viewStateFromHash = () => {
  const parts = window.location.hash.replace(/^#/, '').split('/').map(Number);
  const [zoom, latitude, longitude, pitch, bearing] = parts;
  if (parts.length < 3 || [zoom, latitude, longitude].some(Number.isNaN)) {
    return DEFAULT_VIEWSTATE;
  }
  return {
    longitude,
    latitude,
    zoom,
    pitch: Number.isFinite(pitch) ? pitch : DEFAULT_VIEWSTATE.pitch,
    bearing: Number.isFinite(bearing) ? bearing : 0,
  };
};

const INITIAL_VIEWSTATE = viewStateFromHash();

const getWLI = (area, dateIndex) => (
  area && area.timeline[dateIndex]
    ? (area.timeline[dateIndex] + 100) / 200 : null
);

export default function App() {
  const {
    areas, shapes, viewMode, selectedLocation, nearbyGauges, selectedAttribute,
    discreteSelectedDateIndex, continuousSelectedDateIndex, hexagonOpacity,
    setHexagonOpacity, isMobile
  } = useSessionContext();

  // used to keep track of what was going on before a search result was selected
  const [originalHexagonOpacity, setOriginalHexagonOpacity] = useState();
  const [originalViewState, setOriginalViewState] = useState();

  const [hasAdjustedAngle, setHasAdjustedAngle] = useState(false);
  const [viewState, setViewState] = useState(INITIAL_VIEWSTATE);

  const isAdmin = viewMode === 'adm';

  /* The values and the geometry arrive as separate files, so the join happens here.
   * A Map, not a `.find` -- the fill accessor runs once per area per frame while the
   * timeline animates, and a linear scan over 3,732 areas inside it is 14M comparisons
   * a frame.
   */
  const areasByKey = useMemo(() => {
    const index = new Map();
    (areas || []).forEach((area) => index.set(area.areaKey, area));
    return index;
  }, [areas]);

  const areaForFeature = useCallback((feature) => areasByKey.get(feature.id), [areasByKey]);

  // when a location (from search) finishes loading, center it on the screen with an animation
  useEffect(() => {
    if (!selectedLocation) {
      if (originalHexagonOpacity) setHexagonOpacity(originalHexagonOpacity);
      if (originalViewState) {
        setViewState({
          ...(originalViewState.zoom < 6 ? originalViewState : INITIAL_VIEWSTATE),
          transitionDuration: 500,
          transitionInterpolator: new FlyToInterpolator()
        });
      }
      setOriginalHexagonOpacity();
      setOriginalViewState();
      return;
    }

    setOriginalHexagonOpacity(hexagonOpacity);
    setOriginalViewState(viewState);
    const { lat, lng } = selectedLocation;
    setHexagonOpacity(1);
    const latitudeAdjustment = isMobile ? 0.15 : 0;
    setViewState((current) => ({
      ...current,
      longitude: Number(lng),
      latitude: Number(lat) + latitudeAdjustment,
      zoom: 9,
      bearing: 0,
      pitch: 60,
      transitionDuration: 500,
      transitionInterpolator: new FlyToInterpolator()
    }));
  }, [selectedLocation]);

  const getInterpolatedWLI = useCallback((area) => {
    const dateA = Math.floor(continuousSelectedDateIndex);
    const dateB = Math.ceil(continuousSelectedDateIndex);
    const wliA = getWLI(area, dateA);
    const wliB = getWLI(area, dateB);
    const intermediate = continuousSelectedDateIndex % 1;
    const wliDelta = wliB - wliA;
    return wliA + (wliDelta * intermediate);
  }, [continuousSelectedDateIndex]);

  const getAreaElevation = useCallback((area) => {
    if (!area) return null;
    switch (selectedAttribute) {
      case 'wli':
        return getInterpolatedWLI(area);
      case 'gaugeCount':
        return area.gaugeCountNorm;
      default:
        return null;
    }
  }, [selectedAttribute, getInterpolatedWLI]);

  const getAreaFillColor = useCallback((area) => {
    const { colorMapInterpolator } = ATTRIBUTE_OPTIONS[selectedAttribute];
    const level = getAreaElevation(area);
    // An area with no reading today is transparent, NOT dark. Canada is near-empty by
    // design -- 2,223 of its 2,249 in-scope gauges are ECCC, whose licence bars it from
    // this surface -- and "no data" has to look like absence rather than like a low index.
    if (level === null || Number.isNaN(level)) return [0, 0, 0, 0];
    const color = colorMapInterpolator(level);
    const { r, g, b } = rgb(color);
    return [r, g, b];
  }, [selectedAttribute, getAreaElevation]);

  const getAreaTooltip = useCallback((area, feature) => {
    if (getAreaElevation(area) === null) return null;

    const pluralizedGauges = area.gaugeCount === 1 ? 'gauge' : 'gauges';

    const shouldShowWLI = (
      selectedAttribute === 'wli' && area.timeline[discreteSelectedDateIndex] !== null
    );
    const wliLabel = shouldShowWLI ? `
      <span>Water Level Index: <b>${area.timeline[discreteSelectedDateIndex]}</b></span>
      <br />
    ` : '';

    // The administrative view has a name to show and the hexagon view does not.
    const properties = (feature && feature.properties) || null;
    const kind = properties && properties.l ? ` &middot; ${properties.l}` : '';
    const nameLabel = properties ? `
      <span><b>${properties.n}</b>${kind}</span>
      <br />
    ` : '';

    return {
      html: `
        <div>
          ${nameLabel}
          ${wliLabel}
          <span>${area.gaugeCount} ${pluralizedGauges}</span>
        </div>
      `,
      style: {
        color: 'var(--gray0)',
        backgroundColor: 'rgba(28, 28, 30, 0.4)',
        backdropFilter: 'blur(10px)',
        '-webkit-backdrop-filter': 'blur(10px)'
      }
    };
  }, [selectedAttribute, discreteSelectedDateIndex, getAreaElevation]);

  const getTooltip = useCallback((item) => {
    // for search result "beacon" mouseover
    if (item.name && !item.properties) {
      return {
        html: selectedLocation.name,
        style: {
          'max-width': '200px'
        }
      };
    }

    // a GeoJSON feature in administrative mode, an area record in hexagon mode
    if (item.properties && item.geometry) {
      const area = areaForFeature(item);
      return area ? getAreaTooltip(area, item) : null;
    }
    return getAreaTooltip(item);
  }, [selectedLocation, getAreaTooltip, areaForFeature]);

  // used to get the start/end points of the nearby gauges arcs,
  // so they don't get covered by the areas
  const getAreaPosition = useCallback((lat, lng, areaKey) => {
    const targetArea = areasByKey.get(areaKey);
    const scale = isAdmin ? ADM_ELEVATION_SCALE : ELEVATION_SCALE;
    const elevation = hexagonOpacity < 50
      ? 0 // area is mostly transparent, so it's safe to put stuff on the ground
      : (getAreaElevation(targetArea) || 0) * scale;
    return [lng, lat, elevation];
  }, [areasByKey, hexagonOpacity, getAreaElevation, isAdmin]);

  const hexagonLayer = new H3HexagonLayer({
    id: 'h3-hexagon-layer',
    data: isAdmin ? [] : areas,
    pickable: true,
    stroked: true,
    filled: true,
    opacity: hexagonOpacity / 100,
    extruded: true,
    getHexagon: (area) => area.areaKey,
    getElevation: getAreaElevation,
    getFillColor: getAreaFillColor,
    coverage: 0.999, // prevents depth-fighting when opacity < 1
    updateTriggers: {
      getFillColor: [selectedAttribute, continuousSelectedDateIndex],
      getElevation: [selectedAttribute, continuousSelectedDateIndex]
    },
    getLineColor: [255, 255, 255],
    lineWidthMinPixels: 2,
    elevationScale: ELEVATION_SCALE
  });

  /* The administrative counterpart of the hexagon layer. Same accessors, same timeline,
   * same colour ramp -- the only difference is where the polygon comes from.
   *
   * Only areas that hold a publishable gauge are in the file at all, so an area missing
   * from the map is an area with no data rather than an area that failed to render.
   */
  const adminLayer = new GeoJsonLayer({
    id: 'adm-layer',
    data: isAdmin && shapes ? shapes.features : [],
    pickable: true,
    stroked: true,
    filled: true,
    opacity: hexagonOpacity / 100,
    /* EXTRUDED, like the hexagon layer, on Heiko's instruction (2026-08-31).
     *
     * This was built flat and the reasoning is worth keeping, because it is the thing to
     * look at if the view reads badly: extrusion encodes a value as height, which is
     * unambiguous only when every footprint is the same size. H3 res-4 cells all are.
     * Administrative areas are not -- a German *Stadtkreis* and a Western Australian
     * local government area differ by three orders of magnitude -- so a small dense area
     * at a large elevation scale becomes a needle that occludes its neighbours without
     * meaning anything more than they do.
     *
     * `ADM_ELEVATION_SCALE` is the answer to that and not an afterthought: 30,000
     * against the hexagons' 100,000, chosen as the largest scale at which central Europe
     * still reads as a surface rather than a pin cushion. If the extruded view is hard
     * to read somewhere, lower that constant -- do not go back to flat, and do not raise
     * it to match the hexagons.
     *
     * Not a performance concern: extrusion was measured as a suspected cost and was not
     * one -- removing it changed frame time by under 5%. The cost is pixel fill.
     */
    extruded: true,
    /* THE ACCESSOR TAKES A FEATURE, NOT AN AREA. `GeoJsonLayer` hands accessors the
     * GeoJSON feature, where `H3HexagonLayer` hands them the datum itself -- which is why
     * the hexagon layer can pass `getAreaElevation` directly above and this one cannot.
     * Passing it unwrapped yields `undefined` for every area and deck.gl renders a flat
     * layer with no error, which looks exactly like `extruded: false` still being set.
     *
     * `null` means the area has no reading today. It must flatten to 0 rather than reach
     * the layer: an undefined elevation is NaN in the buffer and takes the polygon with
     * it. The fill is already transparent for these, so 0 renders them as absent, which
     * is the same treatment `getAreaFillColor` gives them and for the same reason --
     * Canada is near-empty by licence, not by fault.
     */
    getElevation: (feature) => {
      const level = getAreaElevation(areaForFeature(feature));
      return level === null || Number.isNaN(level) ? 0 : level;
    },
    /* Interpolated between days and updated every frame, exactly as the hexagon layer
     * is, so the scrubber behaves the same in both views.
     *
     * PERFORMANCE, and the evidence we do not have. Under a headless SOFTWARE renderer
     * this layer runs ~5.8x the hexagon layer's frame time. Removing extrusion and
     * stepping colour per day instead of per frame were both tried and both changed it
     * by under 5% -- which says the cost is pixel fill in the software rasteriser, not
     * JavaScript or buffer uploads, and a real GPU will not have that constraint. It
     * has NOT been measured on real hardware. If it does prove slow there, the levers
     * in order are: trigger `getFillColor` on `discreteSelectedDateIndex` instead of
     * `continuousSelectedDateIndex`, then simplify the geometry further.
     */
    getFillColor: (feature) => getAreaFillColor(areaForFeature(feature)),
    updateTriggers: {
      getFillColor: [selectedAttribute, continuousSelectedDateIndex, areasByKey],
      // Elevation is interpolated between days exactly as colour is, so the scrubber
      // animates height as well as hue. Without this trigger the extrusion is computed
      // once and then never moves, and the map looks correct while being frozen.
      getElevation: [selectedAttribute, continuousSelectedDateIndex, areasByKey]
    },
    /* Borders carry meaning in this view in a way they do not for hexagons: the whole
     * claim is "this is a county / a *Kreis*", and at 60 alpha adjacent areas with
     * similar values merge into one wash with no visible boundary between them. Bright
     * enough to delineate, thin enough not to become a mesh when the whole of Europe is
     * on screen. */
    getLineColor: [255, 255, 255, 150],
    lineWidthMinPixels: 1,
    lineWidthMaxPixels: 2,
    elevationScale: ADM_ELEVATION_SCALE
  });

  const selectedLocationLayer = new LineLayer({
    id: 'search-result-layer',
    data: selectedLocation ? [selectedLocation] : [],
    getSourcePosition: ({ lng, lat }) => [Number(lng), Number(lat)],
    getTargetPosition: ({ lng, lat }) => [Number(lng), Number(lat), 100000000],
    getColor: [199, 199, 201],
    getWidth: 5,
    pickable: true
  });

  const nearbyGaugesLayer = new ArcLayer({
    id: 'nearby-gauges-layer',
    data: selectedLocation ? nearbyGauges : [],
    opacity: 0.8,
    getSourcePosition: () => {
      const { lat, lng, areaKey } = selectedLocation;
      return getAreaPosition(lat, lng, areaKey);
    },
    getTargetPosition: ({ lat, lng, areaKey }) => getAreaPosition(lat, lng, areaKey),
    updateTriggers: {
      getSourcePosition: [getAreaPosition],
      getTargetPosition: [getAreaPosition]
    },
    getSourceColor: [196, 173, 116],
    getTargetColor: [161, 54, 157],
    getWidth: 3,
    pickable: false
  });

  return (
    <>
      <DeckGL
        viewState={viewState}
        controller={{ touchRotate: true }}
        onViewStateChange={({ viewState: newViewState }) => {
          // update tracked view state
          setViewState({ ...newViewState, transitionDuration: 0 });

          // keep track of whether "Hold down shift to rotate" hint can be hidden
          if (newViewState.bearing !== 0) setHasAdjustedAngle(true);
        }}
        layers={[nearbyGaugesLayer, selectedLocationLayer, hexagonLayer, adminLayer]}
        getTooltip={({ object }) => object && getTooltip(object)}
      >
        <MapGL
          mapLib={maplibregl}
          attributionControl={false}
          mapStyle="https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json"
        />
      </DeckGL>
      <StatusBanner />
      <div className="overlay-container top">
        <LeftPanel />
        <RightPanel />
      </div>
      <div
        style={{ bottom: (IS_IPAD && window.innerWidth <= 900) ? '12px' : 0 }}
        className="overlay-container bottom"
      >
        <LogoPanel />
        {selectedAttribute === 'wli' && (
          <TimelineScrubber />
        )}
        <ShiftHint isVisible={!hasAdjustedAngle} />
      </div>
      <MobilePanel />
      <DisclaimerModal />
    </>
  );
}
